
import React, { useState, useEffect } from 'react';
import { AppSection } from '../types';

interface HeroProps {
  onNavigate: (section: AppSection) => void;
  onContactClick: () => void;
}

const Hero: React.FC<HeroProps> = ({ onNavigate, onContactClick }) => {
  const variations = [
    {
      label: "DEPLOYMENT_READY",
      headline: "Building Enterprise Velocity with Chicago Precision.",
      sub: "We merge high-level strategy with technical talent to scale mission-critical systems."
    },
    {
      label: "SYSTEM_SCALING",
      headline: "Managed Talent for the Agile Enterprise.",
      sub: "Our framework ensures projects move from whiteboard to production with Midwest reliability."
    },
    {
      label: "STRATEGIC_DEPTH",
      headline: "Scalable Solutions for the Modern Tech Grid.",
      sub: "Providing the oversight required to solve complex delivery challenges in real-time."
    }
  ];

  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % variations.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 md:py-40 text-white">
      {/* Background Layers */}
      <div className="absolute inset-0 grid-bg-dark opacity-60"></div>
      <div className="absolute inset-0 hero-glow opacity-80"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center space-x-4 mb-6 md:mb-8 animate-in fade-in slide-in-from-bottom-2">
              <div className="mono text-[10px] tracking-[0.2em] text-[#facc15] font-bold px-3 py-1 border border-[#facc15]/40 rounded-sm uppercase bg-[#facc15]/10 yellow-accent-glow">
                {variations[active].label}
              </div>
              <div className="h-[1px] w-12 bg-slate-800"></div>
              <span className="mono text-[10px] text-slate-500 tracking-widest uppercase">Location // Chicago_Grid</span>
            </div>
            
            <div className="relative min-h-[220px] md:min-h-[300px] flex flex-col justify-center">
              <h1 className="text-4xl md:text-7xl lg:text-8xl font-bold leading-[1.1] md:leading-[0.9] mb-6 md:mb-10 tracking-tighter">
                {variations[active].headline.split(' ').map((word, i) => (
                  <span key={`${active}-${i}`} className={`inline-block mr-2 md:mr-4 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both ${word === 'Precision.' || word === 'Enterprise.' || word === 'Grid.' ? 'text-[#0091da]' : ''}`} style={{animationDelay: `${i * 30}ms`}}>
                    {word}
                  </span>
                ))}
              </h1>
              <p className="text-lg md:text-2xl text-slate-400 mb-8 md:mb-10 leading-relaxed max-w-2xl font-light animate-in fade-in slide-in-from-bottom-4 duration-700">
                {variations[active].sub}
              </p>

              <div className="flex items-center space-x-10 mb-10 md:mb-14 animate-in fade-in duration-1000">
                <div className="flex space-x-3">
                  {variations.map((_, i) => (
                    <button 
                      key={i}
                      onClick={() => setActive(i)}
                      className={`h-1 transition-all duration-500 rounded-full ${active === i ? 'w-12 md:w-16 bg-[#0091da]' : 'w-4 md:w-6 bg-slate-800 hover:bg-slate-700'}`}
                    />
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#facc15] animate-pulse"></div>
                    <span className="mono text-[9px] text-slate-500 font-bold tracking-widest uppercase">Engine_Sync: OK</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 md:gap-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
              <button 
                onClick={onContactClick}
                className="btn-brand-primary py-4 md:py-5 px-10 md:px-12 font-bold uppercase tracking-widest text-xs rounded-sm whitespace-nowrap"
              >
                Request a Solution
              </button>
              <button 
                onClick={() => onNavigate(AppSection.Talent)}
                className="group mono text-[10px] md:text-xs text-white hover:text-[#facc15] flex items-center space-x-4 transition-all tracking-widest uppercase py-4 md:py-5"
              >
                <span className="border-b border-white/20 group-hover:border-[#facc15]/50 pb-1">Explore Talent Grid</span>
                <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#facc15] group-hover:bg-[#facc15]/5 transition-all">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </div>
              </button>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative animate-in fade-in zoom-in-95 duration-1000 delay-200">
            {/* Hyper-Technical Strategic SVG Graphic */}
            <div className="relative aspect-square w-full max-w-xl mx-auto animate-float">
               <div className="absolute inset-0 bg-[#0091da]/10 rounded-full blur-[120px] opacity-20"></div>
               <svg viewBox="0 0 600 600" className="w-full h-full text-[#0091da]" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <filter id="glow-hero">
                      <feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <linearGradient id="data-pulse-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
                        <stop offset="50%" stopColor="currentColor" stopOpacity="1" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Deep Structural Grid */}
                  <g className="opacity-[0.05]">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <React.Fragment key={i}>
                        <line x1={i * 30} y1="0" x2={i * 30} y2="600" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="0" y1={i * 30} x2="600" y2={i * 30} stroke="currentColor" strokeWidth="0.5" />
                      </React.Fragment>
                    ))}
                  </g>

                  {/* Multi-layered orbital rings */}
                  <g className="orbitals">
                    <circle cx="300" cy="300" r="280" stroke="currentColor" strokeWidth="0.5" strokeDasharray="1 10" className="opacity-10 animate-[spin_120s_linear_infinite]" />
                    <circle cx="300" cy="300" r="250" stroke="currentColor" strokeWidth="1" strokeDasharray="15 45" className="opacity-20 animate-[spin_80s_linear_infinite_reverse]" />
                    <circle cx="300" cy="300" r="220" stroke="#facc15" strokeWidth="0.5" strokeDasharray="4 20" className="opacity-30 animate-[spin_60s_linear_infinite]" />
                    <circle cx="300" cy="300" r="190" stroke="currentColor" strokeWidth="0.8" strokeDasharray="100 20" className="opacity-10 animate-[spin_40s_linear_infinite_reverse]" />
                  </g>

                  {/* Intricate Neural Circuit Connections */}
                  <g className="connections opacity-40" filter="url(#glow-hero)">
                    {/* Primary strategic trunks */}
                    <path d="M300 300 L450 150 L540 150" stroke="currentColor" strokeWidth="1" strokeDasharray="5 5" />
                    <path d="M300 300 L150 150 L60 150" stroke="currentColor" strokeWidth="1" strokeDasharray="5 5" />
                    <path d="M300 300 L450 450 L540 450" stroke="#facc15" strokeWidth="1" strokeDasharray="5 5" />
                    <path d="M300 300 L150 450 L60 450" stroke="#facc15" strokeWidth="1" strokeDasharray="5 5" />
                    
                    {/* Secondary cross-connections */}
                    <path d="M450 150 L450 450" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 10" />
                    <path d="M150 150 L150 450" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 10" />
                    <path d="M150 300 L450 300" stroke="#facc15" strokeWidth="0.5" strokeDasharray="10 20" />
                    
                    {/* Circuit micro-details */}
                    <path d="M540 150 L570 180 M540 150 L570 120" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M60 150 L30 180 M60 150 L30 120" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M540 450 L570 480 M540 450 L570 420" stroke="#facc15" strokeWidth="1.5" />
                    <path d="M60 450 L30 480 M60 450 L30 420" stroke="#facc15" strokeWidth="1.5" />
                  </g>

                  {/* Animated Data Bursts (Particles moving along paths) */}
                  <g filter="url(#glow-hero)">
                    <circle r="4" fill="white">
                      <animateMotion path="M300 300 L450 150 L540 150" dur="3s" repeatCount="indefinite" />
                    </circle>
                    <circle r="4" fill="#facc15">
                      <animateMotion path="M300 300 L150 450 L60 450" dur="4.2s" repeatCount="indefinite" />
                    </circle>
                    <circle r="3" fill="currentColor">
                      <animateMotion path="M150 300 L450 300" dur="5s" repeatCount="indefinite" />
                    </circle>
                  </g>

                  {/* Central Strategic Engine - Core V1 */}
                  <g transform="translate(300, 300)">
                    {/* Exterior Shielding */}
                    <path d="M-60 0 L0 -60 L60 0 L0 60 Z" stroke="currentColor" strokeWidth="1" className="animate-[spin_20s_linear_infinite] opacity-30" />
                    <path d="M-50 0 L0 -50 L50 0 L0 50 Z" stroke="#facc15" strokeWidth="1.5" className="animate-[spin_15s_linear_infinite_reverse] opacity-50" />
                    
                    {/* Inner Core Mechanism */}
                    <circle r="25" fill="currentColor" className="animate-pulse opacity-10" />
                    <rect x="-15" y="-15" width="30" height="30" stroke="white" strokeWidth="1" className="animate-[spin_8s_linear_infinite]" />
                    <rect x="-10" y="-10" width="20" height="20" stroke="#facc15" strokeWidth="1" className="animate-[spin_12s_linear_infinite_reverse]" />
                    <circle r="5" fill="white" className="animate-pulse" />

                    {/* Tech Data Overlays */}
                    <g className="mono text-[7px] fill-slate-500 opacity-80" transform="translate(70, -50)">
                        <text y="0">SYSTEM_ID: HG-01</text>
                        <text y="12">LATENCY: 0.02MS</text>
                        <text y="24">ENCRYPTION: TLS_v1.3</text>
                        <text y="36">LOAD: STABLE</text>
                    </g>
                  </g>

                  {/* High-Fidelity Strategic Nodes with Labels */}
                  <g className="nodes">
                    {/* Top Right */}
                    <g transform="translate(540, 150)">
                      <circle r="10" fill="currentColor" className="animate-pulse opacity-20" />
                      <circle r="18" stroke="currentColor" strokeWidth="0.5" className="animate-[ping_4s_linear_infinite]" />
                      <circle r="6" fill="currentColor" />
                      <text x="25" y="-5" className="mono text-[10px] fill-slate-400 font-bold uppercase tracking-widest">DEPLOY_V4</text>
                      <text x="25" y="8" className="mono text-[7px] fill-slate-600 uppercase">THROUGHPUT: 1.2GB/S</text>
                    </g>
                    
                    {/* Top Left */}
                    <g transform="translate(60, 150)">
                      <circle r="10" fill="currentColor" className="animate-pulse opacity-20" />
                      <circle r="18" stroke="currentColor" strokeWidth="0.5" className="animate-[ping_5s_linear_infinite]" />
                      <circle r="6" fill="currentColor" />
                      <text x="-125" y="-5" className="mono text-[10px] fill-slate-400 font-bold uppercase tracking-widest text-right">AUTO_SCALE</text>
                      <text x="-115" y="8" className="mono text-[7px] fill-slate-600 uppercase text-right">ACTIVE_NODES: 42</text>
                    </g>
                    
                    {/* Bottom Right */}
                    <g transform="translate(540, 450)">
                      <circle r="10" fill="#facc15" className="animate-pulse opacity-20" />
                      <circle r="18" stroke="#facc15" strokeWidth="0.5" className="animate-[ping_3s_linear_infinite]" />
                      <circle r="6" fill="#facc15" />
                      <text x="25" y="5" className="mono text-[10px] fill-slate-400 font-bold uppercase tracking-widest">STRAT_OPS</text>
                    </g>

                    {/* Bottom Left */}
                    <g transform="translate(60, 450)">
                      <circle r="10" fill="#facc15" className="animate-pulse opacity-20" />
                      <circle r="18" stroke="#facc15" strokeWidth="0.5" className="animate-[ping_3.5s_linear_infinite]" />
                      <circle r="6" fill="#facc15" />
                      <text x="-125" y="5" className="mono text-[10px] fill-slate-400 font-bold uppercase tracking-widest text-right">CORE_GRID</text>
                    </g>
                  </g>

                  {/* Floating Binary Rain Fragments (Background detail) */}
                  <g className="mono text-[9px] fill-[#0091da] opacity-10 font-bold">
                    <text x="120" y="80" className="animate-pulse">101101</text>
                    <text x="450" y="100" className="animate-pulse" style={{animationDelay: '1s'}}>001011</text>
                    <text x="100" y="540" className="animate-pulse" style={{animationDelay: '2s'}}>111010</text>
                    <text x="480" y="520" className="animate-pulse" style={{animationDelay: '0.5s'}}>010111</text>
                  </g>
               </svg>
               
               {/* Digital Scanline Overlay for Texture */}
               <div className="absolute inset-0 scanline opacity-30 animate-[scan_7s_linear_infinite]"></div>
            </div>

            {/* Strategic Performance Overlay Badge */}
            <div className="absolute -bottom-8 -left-8 bg-[#020617] border border-slate-800 p-8 rounded-sm shadow-2xl animate-in slide-in-from-bottom-4 duration-700 delay-500">
               <div className="flex items-center space-x-4 mb-5">
                  <div className="w-3 h-3 rounded-full bg-[#facc15] animate-pulse"></div>
                  <div className="mono text-[11px] text-slate-400 uppercase tracking-widest">Grid Integrity Protocol</div>
               </div>
               <div className="text-lg font-bold tracking-tight mb-5">CHICAGO_ENGINE_ACTIVE</div>
               <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <div className="flex flex-col">
                    <span className="mono text-[9px] text-slate-600 uppercase tracking-tighter">Architecture</span>
                    <span className="text-[11px] font-bold text-[#0091da]">VERIFIED</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="mono text-[9px] text-slate-600 uppercase tracking-tighter">Velocity</span>
                    <span className="text-[11px] font-bold text-[#facc15]">99.2%</span>
                  </div>
               </div>
               <div className="mt-6 pt-6 border-t border-slate-800 flex items-center justify-between">
                  <span className="mono text-[8px] text-slate-700 uppercase">Uptime_Metric</span>
                  <span className="mono text-[8px] text-slate-400 font-bold">482:12:09</span>
               </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
